'use client'

import { Button } from '@/components/ui/button'
import { authClient } from '@/lib/auth-client'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

type AutoSignInProps = {
  callbackURL: string
}

export function AutoSignIn({ callbackURL }: AutoSignInProps) {
  useEffect(() => {
    void authClient.signIn.social({
      provider: 'google',
      callbackURL,
    })
  }, [callbackURL])

  return null
}

type TestRole = 'admin' | 'member'

type TestLoginButtonsProps = {
  callbackURL: string
}

export function TestLoginButtons({ callbackURL }: TestLoginButtonsProps) {
  const router = useRouter()
  const [loadingAction, setLoadingAction] = useState<'admin' | 'member' | 'google' | null>(null)

  const loginAs = async (role: TestRole) => {
    setLoadingAction(role)
    try {
      const response = await fetch('/api/test-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      })
      if (!response.ok) {
        return
      }
      router.push(callbackURL)
      router.refresh()
    } finally {
      setLoadingAction(null)
    }
  }

  const loginWithGoogle = () => {
    setLoadingAction('google')
    void authClient.signIn.social({
      provider: 'google',
      callbackURL,
    })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Sign In</h1>
          <p className="mt-2 text-gray-600">Choose a login option</p>
        </div>

        <div className="space-y-2 rounded-lg bg-white p-4 shadow-md">
          <Button onClick={() => void loginAs('admin')} disabled={loadingAction !== null} className="h-10 w-full">
            {loadingAction === 'admin' ? 'Logging in...' : 'Rick Sanchez (admin)'}
          </Button>

          <Button onClick={() => void loginAs('member')} disabled={loadingAction !== null} variant="outline" className="h-10 w-full">
            {loadingAction === 'member' ? 'Logging in...' : 'Morty Smith (member)'}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">or</span>
            </div>
          </div>

          <Button onClick={loginWithGoogle} disabled={loadingAction !== null} variant="outline" className="h-10 w-full">
            {loadingAction === 'google' ? 'Redirecting to Google...' : 'Login with Google'}
          </Button>
        </div>
      </div>
    </div>
  )
}
